import { useState, type FormEvent } from "react";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { validateEmployeeRow, type EmployeeImportRow } from "@graftus/schema";
import type { SectorWords } from "@/hooks/use-sector-words";
import { PrimaryButton } from "../ui/buttons";
import { TextField } from "../ui/TextField";

interface ManualAddFormProps {
  words: SectorWords;
  /** Existing team values, offered as suggestions. */
  teams: string[];
  onAdd: (row: Omit<EmployeeImportRow, "id">) => void;
}

const EMPTY = { name: "", email: "", mobile: "", team: "", role: "", employeeNumber: "" };

/** Six fields and "Lägg till"; the team sticks between adds, everything else clears. */
export function ManualAddForm({ words, teams, onAdd }: ManualAddFormProps) {
  const { t } = useTranslation("onboarding");
  const [values, setValues] = useState(EMPTY);
  const errors = validateEmployeeRow(values);
  const valid = Object.keys(errors).length === 0;
  const set = (key: keyof typeof EMPTY) => (e: FormEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    onAdd({
      name: values.name.trim(),
      email: values.email.trim(),
      team: values.team.trim(),
      mobile: values.mobile.trim() || undefined,
      role: values.role.trim() || undefined,
      employeeNumber: values.employeeNumber.trim() || undefined,
    });
    setValues({ ...EMPTY, team: values.team });
  };

  const datalistId = "onboarding-manual-teams";
  const suggestions = teams.length > 0 ? teams : [words.deptEx];

  return (
    <form onSubmit={submit} noValidate data-testid="onboarding-manual-form" className="flex flex-col gap-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <TextField id="manual-name" label={t("employees.columns.name")} placeholder="Förnamn Efternamn" autoComplete="off" value={values.name} onChange={set("name")} />
        <TextField id="manual-email" label={t("employees.columns.email")} type="email" inputMode="email" placeholder={`namn@${words.domain}`} autoComplete="off" value={values.email} onChange={set("email")} />
        <TextField id="manual-mobile" label={t("employees.columns.mobile")} type="tel" inputMode="tel" placeholder="070-…" autoComplete="off" value={values.mobile} onChange={set("mobile")} error={values.mobile && errors.mobile ? t("employees.errors.invalidMobile") : null} />
        <TextField id="manual-team" label={words.teamCap} placeholder={words.deptEx} list={datalistId} autoComplete="off" value={values.team} onChange={set("team")} />
        <TextField id="manual-role" label={t("employees.columns.role")} placeholder={words.roleEx} autoComplete="off" value={values.role} onChange={set("role")} />
        <TextField id="manual-employee-number" label={t("employees.columns.employeeNumber")} placeholder="1001" inputMode="numeric" autoComplete="off" value={values.employeeNumber} onChange={set("employeeNumber")} />
      </div>
      <datalist id={datalistId}>
        {suggestions.map((team) => (
          <option key={team} value={team} />
        ))}
      </datalist>
      <div className="flex flex-wrap items-center gap-3">
        <PrimaryButton type="submit" disabled={!valid} className="h-11">
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t("employees.manual.add")}
        </PrimaryButton>
        <span className="text-[12.5px] text-g-ink-500">
          {valid ? t("employees.manual.hint.ok") : t("employees.manual.hint.invalid", { team: words.team })}
        </span>
      </div>
    </form>
  );
}
